import { useTranslation } from '../i18n';
import React from 'react';
import { Button, Space, Tag, Typography, Popconfirm } from 'antd';

const { Text } = Typography;

// DNSSEC 安全轮换状态（2026-10-08）：当前密钥 + 轮换阶段 + 下一步时间。user/admin 共用。
// props: status = getDnssecStatus().data（含 keys / rollover）；onAction(action) 返回 Promise；loading
const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const STATE_TAG = { active: null, published: ['blue', '预发布'], retiring: ['default', '待移除'] };

export const DnssecKeyTags = ({ keys }) => {
    const { t } = useTranslation();
    if (!Array.isArray(keys) || keys.length === 0) return null;
    return (
        <Space size={4} wrap>
            {keys.map((k) => {
                const st = STATE_TAG[k.state];
                return (
                    <Tag key={`${k.role}-${k.keytag}-${k.state}`} color={st ? st[0] : 'green'}>
                        {k.role} {k.keytag}{st ? ` · ${t(st[1])}` : ''}
                    </Tag>
                );
            })}
        </Space>
    );
};

const DnssecRolloverPanel = ({ status, onAction, loading }) => {
    const { t } = useTranslation();
    const r = status?.rollover;
    if (!r) return null;
    const act = (a) => () => onAction && onAction(a);
    const cancelBtn = r.can_cancel && (
        <Popconfirm title={t('Cancel this rotation?')} onConfirm={act('cancel')} okText={t('Cancel rotation')} cancelText={t('Back')}>
            <Button size="small" type="link" danger loading={loading}>{t('Cancel')}</Button>
        </Popconfirm>
    );
    const label = r.type === 'ksk' ? t('KSK rotation') : t('ZSK rotation');

    let body = null;
    if (r.phase === 'prepublish') {
        body = <Text>{t('{{label}}: New key being pre-published; signing will switch automatically {{time}}', { label, time: fmt(r.next_at) })}</Text>;
    } else if (r.phase === 'ds_pending') {
        body = (
            <div>
                <div>{t('{{label}}: Add this DS at your registrar (keep the old DS)', { label })}</div>
                <Text code copyable={{ text: r.ds?.record }} style={{ wordBreak: 'break-all', fontSize: 12 }}>
                    {r.ds?.record}
                </Text>
                <div style={{ marginTop: 6 }}>
                    <Space size={8} wrap>
                        <Button size="small" type="primary" loading={loading} onClick={act('confirm_ds')}>{t('Added')}</Button>
                        {r.registrar?.supported && !r.auto_ds && (
                            <Button size="small" loading={loading} onClick={act('auto_ds')}>{t('Automatically update DS')}</Button>
                        )}
                        {r.auto_ds && <Text type="secondary">{t('Submitted to registrar. Will continue automatically once effective.')}</Text>}
                    </Space>
                </div>
            </div>
        );
    } else if (r.phase === 'ds_wait') {
        body = <Text>{t('{{label}}: New DS confirmed; switching to the new key {{time}}', { label, time: fmt(r.next_at) })}</Text>;
    } else if (r.phase === 'retire') {
        body = <Text>{t('{{label}}: Switched to the new key; the old key will be removed {{time}}', { label, time: fmt(r.next_at) })}</Text>;
    } else if (r.phase === 'done') {
        body = (
            <Space size={8} wrap>
                <Text>
                    {r.old_ds_removed
                        ? t('{{label}} complete. The old DS was automatically deleted', { label: label })
                        : t('{{label}} complete. You can delete the old DS at your registrar (Key Tag {{value}})', { label: label, value: r.old_ds?.key_tag })}
                </Text>
                <Button size="small" type="link" onClick={act('dismiss')} loading={loading}>{t('Got it')}</Button>
            </Space>
        );
    }

    return (
        <div style={{ marginTop: 8 }}>
            {body}
            {cancelBtn}
        </div>
    );
};

export default DnssecRolloverPanel;
